/**
 * Theme resolution and persistence (DEC-130).
 *
 * The palette itself lives in `index.css` and nowhere else. This module only
 * decides WHICH palette is active and lets components read token values when
 * they cannot use a Tailwind class.
 *
 * Precedence: an explicit stored choice, then the OS preference, then dark.
 * Dark is last because DEC-127 made it the product's deliberate ground, so it
 * is what an unconfigured install should render.
 */

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'tdia.theme'

/** Meta tag colour per theme, so mobile browser chrome matches the ground. */
const THEME_COLOR: Record<Theme, string> = {
  dark: '#120D1C',
  light: '#F7F7F7', // the light page (DEC-193)
}

export function storedTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'dark' || v === 'light' ? v : null
  } catch {
    // Private-mode Safari throws on localStorage access. A theme is a
    // preference, not a feature, so this degrades to the OS default.
    return null
  }
}

export function prefersLight(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches
}

export function resolveTheme(): Theme {
  return storedTheme() ?? (prefersLight() ? 'light' : 'dark')
}

// ---------------------------------------------------------------------------
// Applying a theme
// ---------------------------------------------------------------------------

type Listener = (theme: Theme) => void
const listeners = new Set<Listener>()

/**
 * Sets `data-theme` on <html>, which is what every CSS variable block keys off.
 * Also updates the theme-color meta tag, since a dark meta colour on a light
 * page shows up as mismatched browser chrome on mobile.
 */
export function applyTheme(theme: Theme, { persist = true }: { persist?: boolean } = {}): void {
  document.documentElement.setAttribute('data-theme', theme)

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_COLOR[theme])

  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // See storedTheme: the choice simply does not survive a reload.
    }
  }

  listeners.forEach((fn) => fn(theme))
}

export function currentTheme(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark'
  applyTheme(next)
  return next
}

/**
 * Drops the explicit choice so the app follows the OS again (DEC-198, Settings >
 * Appearance > "Match my computer"). The `change` listener in `initTheme` already
 * defers to the OS whenever nothing is stored.
 */
export function followSystemTheme(): Theme {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // See storedTheme.
  }
  const next: Theme = prefersLight() ? 'light' : 'dark'
  applyTheme(next, { persist: false })
  return next
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/**
 * Applies the resolved theme, and follows the OS afterwards for as long as the
 * user has not made an explicit choice. Called once from main.tsx; the inline
 * script in index.html has already set the attribute to avoid a flash, so this
 * is idempotent in the common case.
 */
export function initTheme(): void {
  applyTheme(resolveTheme(), { persist: false })

  if (typeof matchMedia !== 'function') return
  matchMedia('(prefers-color-scheme: light)').addEventListener('change', (e) => {
    if (storedTheme() !== null) return // an explicit choice outranks the OS
    applyTheme(e.matches ? 'light' : 'dark', { persist: false })
  })
}

// ---------------------------------------------------------------------------
// Reading token values from JavaScript
// ---------------------------------------------------------------------------

/**
 * Tokens that SVG, canvas and chart code needs as real colour strings, because
 * a Tailwind class cannot reach an SVG presentation attribute or a gradient
 * stop computed in JS.
 *
 * These are read back out of the CSS variables rather than duplicated as hex
 * literals here. A second copy of the palette in TypeScript is a palette that
 * drifts: DEC-128 had to correct 16 files precisely because colour values had
 * been restated in more places than one.
 */
export const THEME_TOKENS = [
  'ground',
  'panel',
  'raised',
  'ink',
  'body',
  'muted',
  'faint',
  'control',
  'accent',
  'accent-text',
  'critical',
  'high',
  'medium',
  'low',
  'warn',
  'success',
  'progress',
  'series-1',
  'series-2',
  'series-3',
  'series-4',
  'series-5',
  'series-6',
  'decision',
  'critical-soft',
] as const

/**
 * Tokens that are already complete colours (they carry their own alpha) rather
 * than RGB channels. Canvas and chart code needs them too, so they are read
 * verbatim instead of being wrapped in `rgb()`.
 */
export const COMPOSITE_TOKENS = [
  'hairline',
  'hairline-strong',
  'fill-subtle',
  'edge-idle',
  'graph-edge',
  'graph-edge-faint',
] as const

export type ThemeToken = (typeof THEME_TOKENS)[number] | (typeof COMPOSITE_TOKENS)[number]

export type ThemeColors = Record<ThemeToken, string>

/**
 * Resolves every token to an `rgb(r g b)` string usable anywhere a colour is
 * accepted, including SVG attributes and canvas fills.
 */
export function readThemeColors(): ThemeColors {
  const style = getComputedStyle(document.documentElement)
  const out = {} as ThemeColors
  for (const token of THEME_TOKENS) {
    const channels = style.getPropertyValue(`--${token}`).trim()
    // Empty means the variable is missing, which is a build-time mistake
    // rather than a runtime condition. Falling back to currentColor keeps the
    // element visible instead of letting it vanish into the background.
    out[token] = channels ? `rgb(${channels})` : 'currentColor'
  }
  for (const token of COMPOSITE_TOKENS) {
    out[token] = style.getPropertyValue(`--${token}`).trim() || 'currentColor'
  }
  return out
}
